/**
 * V151.17: Canonical User Key Utilities
 * Ensures consistent cache keys across UUID and numeric ID formats
 */

export interface UserIdentifier {
  id?: string | number;
  uuid?: string;
}

/**
 * Converts any user identifier to a canonical string key
 * Handles both UUID and numeric ID formats consistently
 */
export const getCanonicalUserKey = (
  user: UserIdentifier | string | number | null | undefined
): string => {
  if (!user) {
    throw new Error('V151.17: Cannot create canonical key from null/undefined user');
  }

  // Handle direct string/number
  if (typeof user === 'string' || typeof user === 'number') {
    return String(user);
  }

  // Handle object with id/uuid properties
  if (typeof user === 'object') {
    // Prefer UUID over numeric ID for consistency
    const key = user.uuid ?? user.id;
    if (!key) {
      throw new Error('V151.17: User object missing both id and uuid properties');
    }
    return String(key);
  }

  throw new Error(`V151.17: Invalid user identifier type: ${typeof user}`);
};

/**
 * Debug helper to log cache key resolution
 */
export const debugUserKey = (user: any, context: string): string => {
  const key = getCanonicalUserKey(user);
  if (import.meta.env.DEV) {
    console.log(`🔑 V151.17: ${context} - Canonical key: ${key} (from:`, user, ')');
  }
  return key;
};

/**
 * Validates that two user identifiers resolve to the same canonical key
 */
export const validateSameUser = (user1: any, user2: any): boolean => {
  try {
    const key1 = getCanonicalUserKey(user1);
    const key2 = getCanonicalUserKey(user2);
    return key1 === key2;
  } catch {
    return false;
  }
};
